import React, { useEffect, useRef, useState } from "react";
import { Card, Badge, Button, GradientBar, LoadingState, ErrorState } from "@/components/design-system";
import { getLatestReport, generateReport, markReportRead } from "@/lib/aiReports";
import { formatApiError } from "@/lib/api";

const cn = (...xs) => xs.filter(Boolean).join(" ");

function SentimentBar({ value, total }) {
  return (
    <div className="flex items-center gap-3">
      <div className="text-sm w-16 text-[#0B1F3A]">{value.label}</div>
      <div className="flex-1 h-2 rounded-full bg-[#E8F1FF] overflow-hidden">
        <div className="h-full" style={{ width: `${value.pct}%`, background: value.color }} />
      </div>
      <div className="text-sm font-bold text-[#0B1F3A] w-10 text-right">{value.pct}%</div>
    </div>
  );
}

function ThemeCard({ t }) {
  const color = t.sentiment === "positive" ? "#7ED957" : t.sentiment === "negative" ? "#DC2626" : "#20C7E8";
  return (
    <div className="rounded-2xl border border-[#0B1F3A]/10 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />
            <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">{t.sentiment}</span>
            {typeof t.count === "number" && <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">· {t.count} mentions</span>}
          </div>
          <h4 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A] mt-1">{t.name}</h4>
          {t.description && <p className="text-sm text-[#0B1F3A]/75 mt-2">{t.description}</p>}
        </div>
      </div>
      {Array.isArray(t.quotes) && t.quotes.length > 0 && (
        <div className="mt-3 space-y-2">
          {t.quotes.map((q, i) => (
            <blockquote key={i} className="border-l-2 border-[#2D7FF9] pl-3 text-sm italic text-[#0B1F3A]/80">"{q}"</blockquote>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AIReportPanel({ campaignId }) {
  const [state, setState] = useState({ status: "loading", data: null, err: "" });
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const data = await getLatestReport(campaignId);
      setState({ status: "ready", data, err: "" });
      // If it's generating, poll
      if (data?.report?.status === "generating") {
        if (!pollRef.current) pollRef.current = setInterval(load, 3000);
      } else if (pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null;
      }
      // Auto mark-read once when the founder actually sees a ready report.
      if (data?.report?.status === "ready" && !data.report.read_at) {
        try {
          const updated = await markReportRead(campaignId);
          setState((s) => ({ ...s, data: { ...s.data, report: updated } }));
          window.dispatchEvent(new CustomEvent("reports:unread-changed"));
        } catch { /* non-fatal */ }
      }
    } catch (err) { setState({ status: "error", data: null, err: formatApiError(err) }); }
  };
  useEffect(() => { load(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; /* eslint-disable-next-line */ }, [campaignId]);

  const onGenerate = async () => { setBusy(true); try { await generateReport(campaignId); await load(); } catch (err) { alert(formatApiError(err)); } finally { setBusy(false); } };

  if (state.status === "loading") return <Card data-testid="ai-report-card"><LoadingState /></Card>;
  if (state.status === "error") return <Card data-testid="ai-report-card"><ErrorState detail={state.err} /></Card>;

  const { report, approved_count, min_required, can_generate, primary_model } = state.data;

  return (
    <Card data-testid="ai-report-card" className="!p-0 overflow-hidden">
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-2">
          <Badge variant="ai">Feedback report</Badge>
          {report && <Badge variant={report.status === "ready" ? "active" : report.status === "generating" ? "neutral" : "admin"}>{report.status}</Badge>}
        </div>
        <h2 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A] mt-3">Insights from your community, at a glance.</h2>
        <p className="text-sm text-[#0B1F3A]/75 mt-2">Themes, sentiment and a concrete next step, clustered from every approved submission on this campaign. Reports are <b>private to you</b> — nothing is ever shared or published.</p>
      </div>
      <GradientBar />
      <div className="p-6 md:p-8">
        {!report && (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div className="text-sm text-[#0B1F3A]/80">
              {can_generate
                ? `You have ${approved_count} approved submissions — you can generate your first report.`
                : `You need at least ${min_required} approved submissions to generate a report (currently ${approved_count}).`}
            </div>
            <Button data-testid="ai-report-generate" variant="primary" onClick={onGenerate} disabled={!can_generate || busy}>Generate report</Button>
          </div>
        )}

        {report && report.status === "generating" && (
          <div className="flex items-center gap-3 text-sm text-[#0B1F3A]/75">
            <div className="w-4 h-4 rounded-full border-2 border-[#2D7FF9] border-t-transparent animate-spin" />
            Generating with {primary_model}… this usually takes 10–30 seconds.
          </div>
        )}

        {report && report.status === "failed" && (
          <div className="rounded-xl bg-red-50 border border-red-100 p-4">
            <div className="text-sm font-semibold text-red-800">Report failed to generate</div>
            <div className="text-xs text-red-700 mt-1 whitespace-pre-wrap">{report.error_message}</div>
            <div className="mt-3"><Button variant="primary" onClick={onGenerate} disabled={busy}>Try again</Button></div>
          </div>
        )}

        {report && report.status === "ready" && (
          <div className="space-y-6" data-testid="ai-report-ready">
            {/* Sentiment */}
            {report.sentiment_summary && Object.keys(report.sentiment_summary).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Sentiment</div>
                <div className="mt-3 space-y-2 max-w-md">
                  <SentimentBar value={{ label: "Positive", pct: report.sentiment_summary.positive || 0, color: "#7ED957" }} />
                  <SentimentBar value={{ label: "Neutral",  pct: report.sentiment_summary.neutral  || 0, color: "#20C7E8" }} />
                  <SentimentBar value={{ label: "Negative", pct: report.sentiment_summary.negative || 0, color: "#DC2626" }} />
                </div>
              </div>
            )}

            {/* Themes */}
            {Array.isArray(report.themes) && report.themes.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Themes</div>
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="ai-report-themes">
                  {report.themes.map((t, i) => <ThemeCard key={i} t={t} />)}
                </div>
              </div>
            )}

            {/* Pain points + delighters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array.isArray(report.top_pain_points) && report.top_pain_points.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Top pain points</div>
                  <ul className="mt-3 space-y-2 text-sm">
                    {report.top_pain_points.map((p, i) => (
                      <li key={i} className="flex gap-3 text-[#0B1F3A]/85"><span className="text-red-500 font-bold">·</span><span>{p}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(report.top_delighters) && report.top_delighters.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Top delighters</div>
                  <ul className="mt-3 space-y-2 text-sm">
                    {report.top_delighters.map((p, i) => (
                      <li key={i} className="flex gap-3 text-[#0B1F3A]/85"><span className="text-[#7ED957] font-bold">·</span><span>{p}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Likert averages */}
            {report.likert_averages && Object.keys(report.likert_averages).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Likert averages</div>
                <div className="mt-3 flex flex-wrap gap-3">
                  {Object.entries(report.likert_averages).map(([qid, val]) => (
                    <div key={qid} className="rounded-lg bg-[#E8F1FF] px-3 py-2 text-sm">
                      <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55 mr-2">{qid}</span>
                      <b className="text-[#0B1F3A]">{Number(val).toFixed(1)} / 5</b>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendation */}
            {report.recommendation && (
              <div className="rounded-xl border-2 border-[#2D7FF9]/30 bg-[#E8F1FF] p-5">
                <div className="text-[11px] uppercase tracking-widest text-[#2D7FF9] font-bold">Recommended next step</div>
                <p data-testid="ai-report-recommendation" className="mt-2 text-[#0B1F3A] font-medium leading-relaxed">{report.recommendation}</p>
              </div>
            )}

            {/* Meta */}
            <div className="pt-6 border-t border-[#0B1F3A]/10 flex flex-col md:flex-row md:items-center gap-4 justify-between">
              <div className="text-xs text-[#0B1F3A]/60 space-y-0.5">
                <div>{report.submissions_count} submissions clustered · {report.duration_seconds}s</div>
                <div>Generated {report.generated_at ? new Date(report.generated_at).toLocaleString() : "—"}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="ai-report-regenerate" variant="ghost" onClick={onGenerate} disabled={busy}>Regenerate</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
